import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { PictureHtml } from '../../frontend/html/PictureHtml.js';
import { PictureSrcHtml } from '../../frontend/html/PictureSrcHtml.js';
import { CONTAINER_IMG_SIZES } from '../../pictures/IMG_SIZES.js';

type StyleName = 'soft-fade' | 'gentle-zoom' | 'some-wind' | 'mountain-shift' | 'cozy-blur' | 'cycle-all';
const AVAILABLE_STYLES: StyleName[] = ['soft-fade', 'gentle-zoom', 'some-wind', 'mountain-shift', 'cozy-blur'];

/** How the page's CSS fits each image into the container (its object-fit). */
export type PictureFit = 'cover' | 'contain';

/**
 * Width in CSS px the picture takes in a container of the given size - the
 * `sizes` value, so the browser downloads the variant that is really shown.
 * "cover" fills the container's height too (the image may be wider than it),
 * "contain" fits inside it (the image may be narrower).
 */
export function displayedWidth(
    container: { width: number; height: number },
    pic: { width: number; height: number },
    fit: PictureFit,
): number {
    const heightBoundWidth = pic.height > 0 ? Math.ceil((container.height * pic.width) / pic.height) : container.width;
    return fit === 'contain'
        ? Math.min(container.width, heightBoundWidth)
        : Math.max(container.width, heightBoundWidth);
}

@customElement('lit-animated-picture')
export class LitAnimatedPicture extends LitElement {
    @property({ type: Array }) pictures: PictureHtml[] = [];
    @property({ type: Number }) interval = 5 * 1000;
    @property({ type: String }) animationStyle?: StyleName;
    @property({ type: Boolean }) cycleAlpacaStyles = false;
    @property({ type: Number }) cycleInterval = 10 * 1000;
    /** Must match the CSS object-fit of .lit__image where the component is used. */
    @property({ type: String }) fit: PictureFit = 'cover';

    @state() currentIndex = 0;
    @state() private activeStyleIndex = 0;
    @state() private sortedPictures: PictureHtml[] = [];
    // Rozmiar .lit__container w px CSS; null = jeszcze nie zmierzony (obrazy nie są renderowane,
    // żeby przeglądarka nie pobrała wariantu dobranego do 100vw przed poznaniem rozmiaru).
    @state() private containerSize: { width: number; height: number } | null = null;
    // Najwyższy indeks slajdu, którego obraz jest już w DOM: bieżący i następny.
    // Ukryte slajdy leżą jeden na drugim w widocznym obszarze, więc
    // loading="lazy" ich nie wstrzymuje - bez tego karuzela pobierała od razu
    // wszystkie zdjęcia. Kolejny slajd dochodzi przy każdym obrocie.
    @state() private loadedUpTo = 1;

    // Referencja do elementów obrazów w DOM
    private pictureElements: HTMLElement[] = [];
    private pictureTimer?: number;
    private changingPicture = false;
    private styleTimer?: number;
    private resizeObserver?: ResizeObserver;
    private visibilityObserver?: IntersectionObserver;
    // Poza ekranem karuzela się nie obraca - inaczej z czasem i tak pobrałaby
    // wszystkie slajdy, których nikt nie widzi.
    private isVisible = true;

    createRenderRoot() {
        return this; // Brak Shadow DOM
    }

    connectedCallback() {
        super.connectedCallback();
        this.sortedPictures = [...this.pictures].sort((a, b) => b.height - a.height);
        this.startPictureRotation();
        this.setupStyleRotation();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.stopPictureRotation();
        this.stopStyleRotation();
        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;
        this.visibilityObserver?.disconnect();
        this.visibilityObserver = undefined;
    }

    firstUpdated() {
        const container = this.querySelector<HTMLElement>('.lit__container')!;
        const measure = () => {
            const { width, height } = container.getBoundingClientRect();
            this.containerSize = { width: Math.ceil(width), height: Math.ceil(height) };
        };
        measure();
        this.resizeObserver = new ResizeObserver(measure);
        this.resizeObserver.observe(container);

        this.visibilityObserver = new IntersectionObserver(([entry]) => {
            this.isVisible = entry.isIntersecting;
            if (this.isVisible) {
                this.startPictureRotation();
            } else {
                this.stopPictureRotation();
            }
        });
        this.visibilityObserver.observe(container);
    }

    /**
     * Wartość `sizes` dla jednego zdjęcia: szerokość, jaką zajmie w kontenerze
     * przy object-fit z właściwości `fit` (displayedWidth).
     * Ukryty kontener (0 px) dostaje wartość kontenerową z breakpointów strony.
     */
    private getSizes(pic: PictureHtml): string {
        const container = this.containerSize!;
        if (container.width === 0) return CONTAINER_IMG_SIZES;
        return `${displayedWidth(container, pic, this.fit)}px`;
    }

    updated(changedProps: Map<string, any>) {
        // Kluczowe: Aktualizacja referencji do elementów po każdym renderowaniu,
        // jeśli zmieniły się obrazki
        if (changedProps.has('pictures') || changedProps.has('sortedPictures') || changedProps.has('containerSize')) {
            this.pictureElements = Array.from(this.querySelectorAll('.lit__picture'));
        }

        if (this.pictures.length <= 1) return;

        if (changedProps.has('pictures') || changedProps.has('interval')) {
            this.startPictureRotation();
        }

        if (
            changedProps.has('animationStyle') ||
            changedProps.has('cycleAlpacaStyles') ||
            changedProps.has('cycleInterval')
        ) {
            this.setupStyleRotation();
        }
    }

    private getDisplayPictures(): PictureHtml[] {
        if (this.pictures.length <= 1) return this.pictures;
        return this.sortedPictures.length > 0 ? this.sortedPictures : this.pictures;
    }

    /** ------------------------ STYLE ROTATION ------------------------ */
    private setupStyleRotation() {
        this.stopStyleRotation();
        if (this.animationStyle && !this.cycleAlpacaStyles) return;
        this.startStyleRotation();
    }

    private startStyleRotation() {
        this.stopStyleRotation();
        this.styleTimer = window.setInterval(() => {
            this.activeStyleIndex = (this.activeStyleIndex + 1) % AVAILABLE_STYLES.length;
        }, this.cycleInterval);
    }

    private stopStyleRotation() {
        if (this.styleTimer) {
            clearInterval(this.styleTimer);
            this.styleTimer = undefined;
        }
    }

    private get activeStyle(): StyleName {
        return this.animationStyle && !this.cycleAlpacaStyles
            ? this.animationStyle
            : AVAILABLE_STYLES[this.activeStyleIndex];
    }

    /** ------------------------ PICTURE ROTATION ------------------------ */
    private startPictureRotation() {
        this.stopPictureRotation();
        const displayPictures = this.getDisplayPictures();
        if (displayPictures.length <= 1 || !this.isVisible) return;
        this.pictureTimer = window.setInterval(() => this.nextPicture(), this.interval);
    }

    private stopPictureRotation() {
        if (this.pictureTimer) {
            clearInterval(this.pictureTimer);
            this.pictureTimer = undefined;
        }
    }

    private async nextPicture() {
        const displayPictures = this.getDisplayPictures();
        if (this.changingPicture || displayPictures.length <= 1 || this.pictureElements.length === 0) return;
        this.changingPicture = true;
        const oldIndex = this.currentIndex;
        const newIndex = (oldIndex + 1) % displayPictures.length;
        try {
            // Render the upcoming image while keeping the current slide active.
            this.loadedUpTo = Math.max(this.loadedUpTo, newIndex + 1);
            await this.updateComplete;
            const oldItem = this.pictureElements[oldIndex];
            const newItem = this.pictureElements[newIndex];
            const image = newItem?.querySelector<HTMLImageElement>('img');
            if (!oldItem || !newItem || !image) return;
            // The next slide may be lazy-loaded; explicitly request it before
            // waiting for decode so the hidden layer cannot stall rotation.
            image.loading = 'eager';
            await image.decode();
            if (!this.isConnected || !this.isVisible || this.currentIndex !== oldIndex) return;
            this.playAnimation(oldItem, newItem);
            this.currentIndex = newIndex;
        } catch {
            // Keep the current photo when the next one cannot be decoded.
        } finally {
            this.changingPicture = false;
        }
    }

    /** ------------------------ ANIMATIONS ------------------------ */
    private playAnimation(oldEl: HTMLElement, newEl: HTMLElement) {
        const style = this.activeStyle;

        const onFinish = () => {
            oldEl.classList.remove('lit-active');
            newEl.classList.add('lit-active');
        };

        // Natychmiastowa aktualizacja klas przed startem/po starcie animacji
        // (zależnie od pożądanego efektu nakładania się warstw)
        newEl.classList.add('lit-active');

        switch (style) {
            case 'soft-fade':
                this.fade(oldEl, newEl, onFinish);
                break;
            case 'gentle-zoom':
                this.zoom(oldEl, newEl, onFinish);
                break;
            case 'some-wind':
                this.wind(oldEl, newEl, onFinish);
                break;
            case 'mountain-shift':
                this.shift(oldEl, newEl, onFinish);
                break;
            case 'cozy-blur':
                this.blurAnimate(oldEl, newEl, onFinish);
                break;
        }
    }

    private fade(oldEl: HTMLElement, newEl: HTMLElement, onFinish: () => void) {
        oldEl.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 600, easing: 'ease-out', fill: 'forwards' });
        const newAnim = newEl.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: 600,
            easing: 'ease-out',
            fill: 'forwards',
        });
        newAnim.onfinish = onFinish;
    }

    private zoom(oldEl: HTMLElement, newEl: HTMLElement, onFinish: () => void) {
        oldEl.animate(
            [
                { opacity: 1, transform: 'scale(1)' },
                { opacity: 0, transform: 'scale(0.9)' },
            ],
            { duration: 600, easing: 'ease-out', fill: 'forwards' },
        );
        const newAnim = newEl.animate(
            [
                { opacity: 0, transform: 'scale(1.1)' },
                { opacity: 1, transform: 'scale(1)' },
            ],
            {
                duration: 600,
                easing: 'ease-out',
                fill: 'forwards',
            },
        );
        newAnim.onfinish = onFinish;
    }

    private wind(oldEl: HTMLElement, newEl: HTMLElement, onFinish: () => void) {
        oldEl.animate(
            [
                { opacity: 1, transform: 'translateX(0)' },
                { opacity: 0, transform: 'translateX(-40px)' },
            ],
            { duration: 650, easing: 'ease-out', fill: 'forwards' },
        );
        const newAnim = newEl.animate(
            [
                { opacity: 0, transform: 'translateX(40px)' },
                { opacity: 1, transform: 'translateX(0)' },
            ],
            {
                duration: 650,
                easing: 'ease-out',
                fill: 'forwards',
            },
        );
        newAnim.onfinish = onFinish;
    }

    private shift(oldEl: HTMLElement, newEl: HTMLElement, onFinish: () => void) {
        oldEl.animate(
            [
                { opacity: 1, transform: 'translateY(0)' },
                { opacity: 0, transform: 'translateY(30px)' },
            ],
            { duration: 650, easing: 'ease-out', fill: 'forwards' },
        );
        const newAnim = newEl.animate(
            [
                { opacity: 0, transform: 'translateY(-30px)' },
                { opacity: 1, transform: 'translateY(0)' },
            ],
            {
                duration: 650,
                easing: 'ease-out',
                fill: 'forwards',
            },
        );
        newAnim.onfinish = onFinish;
    }

    private blurAnimate(oldEl: HTMLElement, newEl: HTMLElement, onFinish: () => void) {
        oldEl.animate(
            [
                { opacity: 1, filter: 'blur(0px)' },
                { opacity: 0, filter: 'blur(6px)' },
            ],
            { duration: 700, easing: 'ease-out', fill: 'forwards' },
        );
        const newAnim = newEl.animate(
            [
                { opacity: 0, filter: 'blur(6px)' },
                { opacity: 1, filter: 'blur(0px)' },
            ],
            {
                duration: 700,
                easing: 'ease-out',
                fill: 'forwards',
            },
        );
        newAnim.onfinish = onFinish;
    }

    private renderPicture(pic: PictureHtml, index: number) {
        const srcSet = new PictureSrcHtml(pic.pictureData).getSrcSet();
        const sizes = this.getSizes(pic);

        return html`
            <picture>
                <source srcset="${srcSet.avif}" sizes="${sizes}" type="image/avif" />
                <source srcset="${srcSet.webp}" sizes="${sizes}" type="image/webp" />
                <img
                    class="lit__image"
                    src="${srcSet.fallbackSrc}"
                    srcset="${srcSet.jpg}"
                    sizes="${sizes}"
                    alt="${pic.alt || ''}"
                    loading="${index === 0 ? 'eager' : 'lazy'}"
                    decoding="${index === 0 ? 'sync' : 'async'}"
                />
            </picture>
        `;
    }

    render() {
        const picturesToRender = this.containerSize ? this.getDisplayPictures() : [];

        return html`
            <div class="lit__container">
                ${picturesToRender.map((pic, index) => {
                    // Ten sam element .lit__picture dla każdego slajdu (pictureElements
                    // trzyma do niego referencje) - warunkowe jest tylko wnętrze.
                    return html`
                    <div class="lit__picture ${index === this.currentIndex ? 'lit-active' : ''}">
                        ${index > this.loadedUpTo ? nothing : this.renderPicture(pic, index)}
                    </div>
                `;
                })}
            </div>
        `;
    }
}
